import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Returning from Google OAuth: let AuthCallback exchange session_id first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const loginWithGoogle = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    setUser(data.user);
    return data.user;
  }, []);

  // Self-branded Google OAuth: exchange the authorization code for a session.
  const googleExchange = useCallback(async (code, redirectUri) => {
    const { data } = await api.post("/auth/google/exchange", { code, redirect_uri: redirectUri });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data;
  }, []);

  const verifyOtp = useCallback(async (email, code) => {
    const { data } = await api.post("/auth/verify-otp", { email, code });
    setUser(data.user);
    return data;
  }, []);

  const resendOtp = useCallback(async (email) => {
    const { data } = await api.post("/auth/resend-otp", { email });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(null);
  }, []);

  const impersonate = useCallback(async (userId) => {
    const { data } = await api.post(`/admin/impersonate/${userId}`);
    setUser(data.user);
    return data.user;
  }, []);

  const stopImpersonate = useCallback(async () => {
    const { data } = await api.post("/admin/stop-impersonate");
    setUser(data.user);
    return data.user;
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, login, loginWithGoogle, googleExchange, register, verifyOtp, resendOtp, logout, refresh, impersonate, stopImpersonate }),
    [user, loading, login, loginWithGoogle, googleExchange, register, verifyOtp, resendOtp, logout, refresh, impersonate, stopImpersonate]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
